import { HttpRequestConfig } from '@okapi/http';

export class FetchApiClient {
  private host: string;

  private readonly defaultHeaders: Record<string, string>;

  constructor() {
    this.defaultHeaders = {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json;charset=UTF-8',
    };
  }

  private buildFullUrl(path: string, params?: unknown): string {
    const normalizedHost = this.host.endsWith('/') ? this.host : this.host + '/';
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    const fullUrl = normalizedHost + normalizedPath;
    const url = new URL(fullUrl);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  private async _request<TResponse = unknown>(
    method: 'GET' | 'POST' | 'DELETE',
    path: string,
    config?: {
      params?: unknown;
      body?: unknown;
      options?: HttpRequestConfig;
    }
  ): Promise<TResponse> {
    const { params, body, options } = config || {};
    const url = this.buildFullUrl(path, params);

    const response = await fetch(url, {
      method,
      headers: {
        ...this.defaultHeaders,
        ...(options?.headers as Record<string, string>),
      },
      credentials: options?.withCredentials ? 'include' : 'same-origin',
      ...(body ? { body: JSON.stringify(body) } : {}),
      ...(method === 'POST' && { keepalive: true }),
    });

    if (!response.ok) {
      throw new Error(`ApiClient HTTP error with status: ${response.status}`);
    }

    const contentLength = response.headers.get('Content-Length');

    if (contentLength && parseInt(contentLength) === 0) {
      return Promise.resolve() as Promise<TResponse>;
    }

    return response.json() as Promise<TResponse>;
  }

  public setHost(host: string) {
    this.host = host;
  }

  public addDefaultHeader(key: string, value: string) {
    this.defaultHeaders[key] = value;
  }

  public async get<
    TResponse = unknown,
    TPath extends string = string,
    TParams = unknown,
    TOptions extends HttpRequestConfig = HttpRequestConfig
  >(path: TPath, params?: TParams, options?: TOptions): Promise<TResponse> {
    return this._request<TResponse>('GET', path, { params, options });
  }

  public async post<
    TResponse = unknown,
    TPath extends string = string,
    TBody = unknown,
    TOptions extends HttpRequestConfig = HttpRequestConfig
  >(path: TPath, body: TBody, options?: TOptions): Promise<TResponse> {
    return this._request<TResponse>('POST', path, { body, options });
  }

  public async delete<
    TResponse = unknown,
    TPath extends string = string,
    TParams = unknown,
    TOptions extends HttpRequestConfig = HttpRequestConfig
  >(path: TPath, params?: TParams, options?: TOptions): Promise<TResponse> {
    return this._request<TResponse>('DELETE', path, { params, options });
  }
}

export const fetchApiClient = new FetchApiClient();
